import { Plus } from "lucide-react";
import { Link } from "react-router-dom";
import { Header } from "@/components/Layout/Header";
import { Button } from "@/components/ui/button";
import { HelpTooltip } from "@/components/ui/help-tooltip";
import { KanbanBoard } from "@/components/Dashboard/KanbanBoard";
import { useEvents } from "@/hooks/useEvents";

export function DashboardPage() {
  const { data: events = [] } = useEvents();

  const stats = {
    total: events.length,
    inProgress: events.filter(
      (e) => !["Completed", "Abandoned"].includes(e.status),
    ).length,
    completed: events.filter((e) => e.status === "Completed").length,
    thisMonth: events.filter((e) => {
      const eventDate = new Date(e.event_date);
      const now = new Date();
      return (
        eventDate.getMonth() === now.getMonth() &&
        eventDate.getFullYear() === now.getFullYear()
      );
    }).length,
  };

  return (
    <div className="flex h-full flex-col">
      <Header title="Dashboard" />

      <div className="flex-1 space-y-6 p-4 md:p-6">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4 md:gap-4">
            <StatCard
              label="Total événements"
              value={stats.total}
              tooltip="Nombre total d'événements dans le système, tous statuts confondus."
            />
            <StatCard
              label="En cours"
              value={stats.inProgress}
              tooltip="Événements actifs (du statut Projet jusqu'à Organisation finale)."
            />
            <StatCard
              label="Terminés"
              value={stats.completed}
              tooltip="Événements dont le statut est Terminé."
            />
            <StatCard
              label="Ce mois"
              value={stats.thisMonth}
              tooltip="Événements programmés pour le mois en cours."
            />
          </div>

          <Link to="/events?new=true" className="shrink-0">
            <Button className="w-full md:w-auto">
              <Plus className="mr-2 h-4 w-4" />
              Nouvel événement
            </Button>
          </Link>
        </div>

        <div>
          <h3 className="mb-4 text-lg font-semibold">
            Pipeline des événements
          </h3>
          <KanbanBoard />
        </div>
      </div>
    </div>
  );
}

function StatCard({
  label,
  value,
  tooltip,
}: {
  label: string;
  value: number;
  tooltip: string;
}) {
  return (
    <div className="rounded-lg border bg-card p-3 md:p-4">
      <div className="flex items-center gap-1">
        <p className="text-xs text-muted-foreground md:text-sm">{label}</p>
        <HelpTooltip content={tooltip} />
      </div>
      <p className="text-xl font-bold md:text-2xl">{value}</p>
    </div>
  );
}
